"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useAnimationFrame, useMotionValue } from "motion/react";

export function useVoicePlayback(duration: number, url?: string) {
  const progress = useMotionValue(0);
  const audio = useRef<HTMLAudioElement | null>(null);
  const [playing, updatePlaying] = useState(false);
  const [error, setError] = useState("");
  const [source, setSource] = useState({ url, duration });
  if (source.url !== url || source.duration !== duration) {
    setSource({ url, duration }); updatePlaying(false); setError("");
  }
  useEffect(() => {
    progress.set(0);
    if (!url) return;
    const player = new Audio(url);
    audio.current = player;
    player.ontimeupdate = () => progress.set(Math.min(1, player.currentTime / Math.max(.1, duration)));
    player.onended = () => { progress.set(1); updatePlaying(false); };
    player.onplay = () => updatePlaying(true);
    player.onpause = () => updatePlaying(false);
    player.onerror = () => { updatePlaying(false); setError("This voice note could not be played."); };
    return () => {
      player.onplay = player.onpause = player.onended = player.ontimeupdate = player.onerror = null;
      player.pause(); player.removeAttribute("src"); player.load();
      if (audio.current === player) audio.current = null;
    };
  }, [url, duration, progress]);
  useAnimationFrame((_, delta) => {
    if (url || !playing) return;
    const next = progress.get() + delta / 1000 / Math.max(.1, duration);
    progress.set(Math.min(1, next));
    if (next >= 1) updatePlaying(false);
  });
  function seek(value: number) {
    const next = Math.min(1, Math.max(0, value));
    progress.set(next);
    if (audio.current) audio.current.currentTime = next * duration;
  }
  function setPlaying(next: boolean) {
    if (!audio.current) { updatePlaying(next); return; }
    if (!next) { audio.current.pause(); return; }
    setError("");
    void audio.current.play().catch(() => { updatePlaying(false); setError("This voice note could not be played. Try again."); });
  }
  function toggle() { if (!playing && progress.get() >= 1) seek(0); setPlaying(!playing); }
  return { progress, playing, toggle, setPlaying, seek, error };
}

type Capture = { recorder: MediaRecorder; stream: MediaStream; context: AudioContext | null; analyser: AnalyserNode | null; samples: Uint8Array<ArrayBuffer> | null; chunks: Blob[]; levels: number[]; generation: number };

export function useVoiceCapture() {
  const capture = useRef<Capture | null>(null);
  const generation = useRef(0);
  const alive = useRef(false);
  const [error, setError] = useState("");
  const urls = useRef(new Set<string>());
  const release = useCallback((session: Capture) => {
    session.stream.getTracks().forEach(track => track.stop());
    if (session.context && session.context.state !== "closed") void session.context.close().catch(() => {});
    if (capture.current === session) capture.current = null;
  }, []);
  const cancel = useCallback(() => {
    generation.current++;
    const session = capture.current;
    if (!session) return;
    if (session.recorder.state !== "inactive") session.recorder.stop();
    release(session);
  }, [release]);
  useEffect(() => {
    const ownedUrls = urls.current;
    alive.current = true;
    return () => { alive.current = false; cancel(); ownedUrls.forEach(url => URL.revokeObjectURL(url)); ownedUrls.clear(); };
  }, [cancel]);
  async function start() {
    cancel();
    setError("");
    const token = generation.current;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") throw new Error("Microphone recording is unavailable in this browser.");
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    if (!alive.current || token !== generation.current) { stream.getTracks().forEach(track => track.stop()); return false; }
    let session: Capture | null = null;
    try {
      const recorder = new MediaRecorder(stream);
      session = { recorder, stream, context: null, analyser: null, samples: null, chunks: [], levels: [], generation: token };
      capture.current = session;
      if (typeof AudioContext !== "undefined") {
        try {
          session.context = new AudioContext(); session.analyser = session.context.createAnalyser(); session.analyser.fftSize = 256;
          session.samples = new Uint8Array(256); session.context.createMediaStreamSource(stream).connect(session.analyser);
          void session.context.resume().catch(() => {});
        } catch { /* Audio recording still works when waveform analysis is unavailable. */ }
      }
      if (!alive.current || token !== generation.current) { release(session); return false; }
      const active = session;
      recorder.onerror = () => {
        release(active);
        if (alive.current && token === generation.current) setError("Recording stopped unexpectedly. Please try again.");
      };
      recorder.ondataavailable = event => { if (event.data.size) active.chunks.push(event.data); };
      recorder.start();
      return true;
    } catch (error) { if (session) release(session); else stream.getTracks().forEach(track => track.stop()); throw error; }
  }
  function sample(elapsed: number) {
    const session = capture.current;
    if (!session) return [];
    let level = .08;
    if (session.analyser && session.samples) {
      session.analyser.getByteTimeDomainData(session.samples);
      const mean = session.samples.reduce((sum, value) => sum + ((value - 128) / 128) ** 2, 0) / session.samples.length;
      level = Math.min(1, Math.max(.07, Math.sqrt(mean) * 4));
    }
    session.levels[Math.floor(elapsed / 90)] = level;
    return session.levels;
  }
  function stop(): Promise<{ audioUrl: string; blob: Blob; peaks: number[] } | null> {
    const session = capture.current;
    if (!session || session.recorder.state === "inactive") return Promise.resolve(null);
    return new Promise((resolve, reject) => {
      session.recorder.onerror = () => { release(session); reject(new Error("The voice note could not be recorded. Try again.")); };
      session.recorder.onstop = () => {
        release(session);
        if (!alive.current || session.generation !== generation.current) { resolve(null); return; }
        const blob = new Blob(session.chunks, { type: session.recorder.mimeType || "audio/webm" });
        if (!blob.size) { reject(new Error("The recording is empty. Try again.")); return; }
        const audioUrl = URL.createObjectURL(blob); urls.current.add(audioUrl);
        const peaks = Array.from({ length: 40 }, (_, index) => {
          const start = Math.floor(index * session.levels.length / 40), end = Math.max(start + 1, Math.floor((index + 1) * session.levels.length / 40));
          return Math.max(.07, ...Array.from(session.levels.slice(start, end), value => value || .07));
        });
        resolve({ audioUrl, blob, peaks });
      };
      session.recorder.stop();
    });
  }
  return { start, stop, cancel, sample, error };
}
